import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseWrapper from 'components/atoms/base-wrapper';
import { SearchFolderIcon } from 'components/atoms/icons/search-folder';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseSelectBox from 'components/atoms/base-select-box';
import BaseButton from 'components/atoms/baseButton';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useCheckPatientModule } from 'infrastructure/hooks/patient/use-check-patient-module';
import PatientSelectBox from 'components/molecules/patient-select-box';
import BaseModal from 'components/atoms/base-modal';
import CallDashboardDetailTabs from 'components/organisms/call-dashboard/tabbed-forms';
import InfoIcon from 'components/atoms/icons/infoIcon';

import s from './styles.module.scss';

import type { PatientSelect } from 'infrastructure/classes/select/patient-select';
import type { IBaseSelectBoxOption } from 'components/atoms/base-select-box/types';

export interface ICallDashboardCallTypeFormValues {
  isRpm: boolean;
  patientGuid?: string;
}

interface IProps {
  patients: PatientSelect[];
  sending?: boolean;
  isUnknownNumber?: boolean;
  patientGuid?: string;
  onSubmit: (data: ICallDashboardCallTypeFormValues) => void;
}

const CallDashboardCallTypeForm: React.FC<IProps> = (props) => {
  const { patients, sending, isUnknownNumber, patientGuid, onSubmit } = props;
  const { t } = useTranslation();

  const [modalOpened, setModalOpened] = useState(false);

  const formik = useFormik<ICallDashboardCallTypeFormValues>({
    initialValues: {
      isRpm: true,
      patientGuid,
    },
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const {
    isBlockRpmModule,
    isReadonlyRpmModule,
    isBlockCcmModule,
    isReadonlyCcmModule,
    loading,
  } = useCheckPatientModule(formik.values.patientGuid ?? '');

  const isRpmDisabled = isBlockRpmModule || isReadonlyRpmModule;
  const isCcmDisabled = isBlockCcmModule || isReadonlyCcmModule;

  const disableSubmit =
    !formik.values.patientGuid || loading || (isRpmDisabled && isCcmDisabled);

  const patientsOptions: IBaseSelectBoxOption[] = useMemo(() => {
    if (!patients) return [];
    return patients.map((el) => ({
      label: `${el.label} ${DateFormatter({
        date: el.birthDate,
        format: DateFormats['MM-dd-yyyy'],
      })}`,
      value: el.guid,
    }));
  }, [patients]);

  const hasPatients = !!patientsOptions.length;

  const handleShowInfo = () => {
    setModalOpened(true);
  };

  useEffect(() => {
    formik.setFieldValue('isRpm', !isRpmDisabled);
  }, [patientGuid, isRpmDisabled, isCcmDisabled, loading]);

  return (
    <BaseWrapper className={s.wrapper}>
      <div className={s.top}>
        <SearchFolderIcon />
        <span className={s.message}>
          {t('messages.callSpecifyService')} <br />
          {(hasPatients || isUnknownNumber) && t('messages.callSelectPatient')}
        </span>
      </div>
      <div className={s.bottom}>
        <BaseForm formik={formik} className={s.form} loading={sending}>
          {hasPatients && (
            <div className={s.input}>
              <BaseFormItem
                wrapperClassName={s.select}
                name="patientGuid"
                label={t('labels.patient')}
              >
                <BaseSelectBox options={patientsOptions} />
              </BaseFormItem>
              <BaseButton
                disabled={!formik.values.patientGuid}
                className={s.btn}
                type="secondary"
                icon={<InfoIcon />}
                onClick={handleShowInfo}
              />
            </div>
          )}
          {isUnknownNumber && (
            <div className={s.input}>
              <BaseFormItem
                wrapperClassName={s.select}
                name="patientGuid"
                label={t('labels.patient')}
              >
                <PatientSelectBox
                  placeholder={t('labels.searchPatient')}
                  allowClear
                  labelRender={(el) => {
                    const date = DateFormatter({
                      date: el.record?.birthDate,
                      format: DateFormats['MM-dd-yyyy'],
                      toUTC: true,
                    });
                    return `${el.label} ${date}`;
                  }}
                />
              </BaseFormItem>
              <BaseButton
                disabled={!formik.values.patientGuid}
                className={s.btn}
                type="secondary"
                icon={<InfoIcon />}
                onClick={handleShowInfo}
              />
            </div>
          )}
          <BaseFormItem name="isRpm" label={t('labels.service')}>
            <BooleanSelectBox
              disabled={isRpmDisabled || isCcmDisabled}
              trueLabel={t('labels.rpm')}
              falseLabel={t('labels.ccm')}
            />
          </BaseFormItem>
          <BaseButton
            loading={sending}
            disabled={disableSubmit}
            className={s.submit}
            width="100%"
            label={t('controls.submit')}
            onClick={formik.submitForm}
          />
        </BaseForm>
      </div>
      <BaseModal
        width="80vw"
        open={modalOpened}
        onCancel={() => setModalOpened(false)}
      >
        <CallDashboardDetailTabs
          isUnknownNumber={false}
          patients={[]}
          patientGuid={formik.values.patientGuid!}
        />
      </BaseModal>
    </BaseWrapper>
  );
};

export default CallDashboardCallTypeForm;
